/**
 * The top search (PR 6a): clients, staff and the Directory from the top
 * bar, grouped, each group only for a seat that can read it.
 *
 *   Clients    GET /clients (useClientsList), when the seat holds
 *              view_all_clinical or view_engagement_clients; matched in
 *              the browser over the seat's own list, which the server
 *              never caps.
 *   Staff      GET /team-directory (useTeamDirectory), when the seat holds
 *              view_staff_tools.
 *   Directory  GET /providers/search, every seat, as the Directory page.
 *
 * A Directory editor holds neither of the first two, so it sees the
 * Directory group alone and its browser never asks for clients or staff.
 * Clients and Staff load once, when the panel first opens, under the
 * query keys the rest of the app shares; the Directory is asked per query,
 * debounced, from two characters. One matching rule for clients and staff
 * (src/lib/nameSearch.ts, the Clients page's), each group on its own name
 * fields (src/lib/globalSearch.ts).
 *
 * A combobox over one listbox: Down and Up move across every row, group
 * after group; Enter opens the highlighted row; Escape closes (the house
 * Dialog's own close). A polite live region says how many rows each group
 * found. A group whose read failed shows its own read sentence, and the
 * other groups still render.
 */

import { useEffect, useMemo, useState, type KeyboardEvent, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Dialog } from '@/components/primitives/Dialog';
import { SearchField } from '@/components/primitives/SearchField';
import { useTenant } from '@/lib/auth';
import { canOpenEveryClientRecord, canReadClinicalRecord, canViewAllClinical, canViewEngagementClients, canViewStaffTools } from '@/lib/capabilities';
import { useClientsList } from '@/lib/clientsApi';
import { useTeamDirectory } from '@/lib/teamDirectory';
import { api } from '@/lib/api';
import { pageReadText } from '@/lib/errorCopy';
import { useSupportContact } from '@/hooks/useSupportContact';
import {
  GLOBAL_SEARCH_MIN_CHARS,
  directoryRows,
  matchClients,
  matchStaff,
  resultsSentence,
  type GlobalSearchKind,
  type GlobalSearchRow,
} from '@/lib/globalSearch';

const INPUT_ID = 'global-search-input';
const LIST_ID = 'global-search-results';
const DEBOUNCE_MS = 250;

/** Which groups a seat sees: each by the helper its read's route enforces. */
export function globalSearchGroups(whoami: Parameters<typeof canViewStaffTools>[0]): {
  clients: boolean;
  staff: boolean;
} {
  return {
    // A client result opens the record, so the group also needs it.
    clients: (canViewAllClinical(whoami) || canViewEngagementClients(whoami)) && canReadClinicalRecord(whoami),
    staff: canViewStaffTools(whoami),
  };
}

const GROUP_TITLE: Record<GlobalSearchKind, string> = {
  client: 'Clients',
  staff: 'Staff',
  directory: 'Directory',
};
const GROUP_TESTID: Record<GlobalSearchKind, string> = {
  client: 'global-search-clients',
  staff: 'global-search-staff',
  directory: 'global-search-directory',
};
const READ_NOUN: Record<GlobalSearchKind, string> = {
  client: 'clients',
  staff: 'staff',
  directory: 'the Directory',
};

export interface GlobalSearchProps {
  onClose: () => void;
}

export function GlobalSearch({ onClose }: GlobalSearchProps): ReactNode {
  const { whoami, tenantSlug } = useTenant();
  const contact = useSupportContact();
  const navigate = useNavigate();
  const groups = globalSearchGroups(whoami);
  const [query, setQuery] = useState('');
  const [debounced, setDebounced] = useState('');
  const [active, setActive] = useState(0);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(query), DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [query]);

  const clientsRead = useClientsList(tenantSlug, { enabled: groups.clients });
  const staffRead = useTeamDirectory(tenantSlug, { enabled: groups.staff });
  const searching = debounced.trim().length >= GLOBAL_SEARCH_MIN_CHARS;
  const directoryRead = useQuery({
    queryKey: ['global-search-directory', tenantSlug, debounced.trim()],
    queryFn: ({ signal }) => api.searchProviders(tenantSlug, { q: debounced.trim(), limit: 5 }, signal),
    enabled: searching,
    staleTime: 30_000,
  });

  const sections = useMemo(() => {
    const out: Array<{ kind: GlobalSearchKind; rows: GlobalSearchRow[]; failed: unknown }> = [];
    if (!searching) return out;
    if (groups.clients) {
      out.push({
        kind: 'client',
        rows: clientsRead.isError ? [] : matchClients(clientsRead.clients, debounced, tenantSlug),
        failed: clientsRead.isError ? clientsRead.error : null,
      });
    }
    if (groups.staff) {
      const people = staffRead.data?.people ?? [];
      out.push({
        kind: 'staff',
        rows: staffRead.isError ? [] : matchStaff(people, debounced, tenantSlug, canOpenEveryClientRecord(whoami)),
        failed: staffRead.isError ? staffRead.error : null,
      });
    }
    out.push({
      kind: 'directory',
      rows: directoryRead.isError ? [] : directoryRows(directoryRead.data?.rows ?? [], tenantSlug),
      failed: directoryRead.isError ? directoryRead.error : null,
    });
    return out;
  }, [searching, groups.clients, groups.staff, clientsRead, staffRead, directoryRead, debounced, tenantSlug, whoami]);

  const flat = sections.flatMap((s) => s.rows);
  useEffect(() => setActive(0), [debounced]);

  const rowId = (r: GlobalSearchRow): string => `global-search-row-${r.kind}-${r.id}`;
  const open = (r: GlobalSearchRow): void => {
    onClose();
    navigate(r.href);
  };
  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>): void => {
    if (flat.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive((i) => (i + 1) % flat.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((i) => (i - 1 + flat.length) % flat.length);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const r = flat[active];
      if (r) open(r);
    }
  };

  const status = searching
    ? resultsSentence({
        ...(groups.clients ? { client: sections.find((s) => s.kind === 'client')?.rows.length ?? 0 } : {}),
        ...(groups.staff ? { staff: sections.find((s) => s.kind === 'staff')?.rows.length ?? 0 } : {}),
        directory: sections.find((s) => s.kind === 'directory')?.rows.length ?? 0,
      })
    : '';
  const highlighted = flat[active];

  return (
    <Dialog
      title={groups.clients || groups.staff ? 'Search clients, staff and the Directory' : 'Search the Directory'}
      size="md"
      testId="global-search"
      onClose={onClose}
      initialFocusId={INPUT_ID}
    >
      <div className="space-y-3 py-1">
        <label htmlFor={INPUT_ID} className="sr-only">
          Search
        </label>
        <SearchField
          id={INPUT_ID}
          testId="global-search-input"
          placeholder="Search names"
          role="combobox"
          aria-expanded={flat.length > 0}
          aria-controls={LIST_ID}
          aria-activedescendant={highlighted ? rowId(highlighted) : undefined}
          value={query}
          onChange={setQuery}
          onKeyDown={onKeyDown}
          className="w-full"
        />
        <p className="sr-only" role="status" aria-live="polite" data-testid="global-search-status">
          {status}
        </p>
        {searching && (
          <div id={LIST_ID} role="listbox" aria-label="Results" className="space-y-4">
            {sections.map((s) => (
              <div
                key={s.kind}
                role="group"
                aria-labelledby={`global-search-heading-${s.kind}`}
                data-testid={GROUP_TESTID[s.kind]}
              >
                <h3 id={`global-search-heading-${s.kind}`} className="text-xs font-semibold uppercase tracking-wide text-sage-800">
                  {GROUP_TITLE[s.kind]}
                </h3>
                {s.failed ? (
                  <p className="mt-1 text-sm text-pepper-deep">{pageReadText(s.failed, contact, READ_NOUN[s.kind])}</p>
                ) : s.rows.length === 0 ? (
                  <p className="mt-1 text-sm text-ink-soft">No matches</p>
                ) : (
                  <ul className="mt-1">
                    {s.rows.map((r) => {
                      const selected = highlighted !== undefined && rowId(highlighted) === rowId(r);
                      return (
                        <li
                          key={rowId(r)}
                          id={rowId(r)}
                          role="option"
                          aria-selected={selected}
                          data-testid={rowId(r)}
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => open(r)}
                          className={`cursor-pointer rounded-tight px-2 py-1.5 break-words ${selected ? 'bg-sage-50' : 'hover:bg-sand'}`}
                        >
                          <span className="block text-sm font-medium text-ink">{r.label}</span>
                          {r.detail !== null && <span className="block text-xs text-ink-soft">{r.detail}</span>}
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            ))}
            {flat.some((r) => r.kind === 'directory') && (
              <p className="text-sm">
                <a
                  href={`/t/${tenantSlug}/directory?q=${encodeURIComponent(debounced.trim())}`}
                  onClick={(e) => {
                    e.preventDefault();
                    onClose();
                    navigate(`/t/${tenantSlug}/directory?q=${encodeURIComponent(debounced.trim())}`);
                  }}
                  className="font-semibold text-sage-800 underline"
                  data-testid="global-search-directory-all"
                >
                  See all in the Directory
                </a>
              </p>
            )}
          </div>
        )}
      </div>
    </Dialog>
  );
}
